import { useState, useEffect, useCallback, useRef } from 'preact/hooks'
import { useSearch, useLocation } from 'wouter-preact'
import { Seite } from '../app.jsx'
import { api } from '../lib/api.js'
import { KartenMarkdown } from '../lib/markdown.jsx'
import { Laden } from '../components/Zustand.jsx'

/**
 * Karten anlegen und bearbeiten, mit Live-Vorschau.
 *
 * Die Vorschau nutzt denselben Renderer wie der Lernbildschirm und dieselbe
 * Breite — sonst wäre sie eine Behauptung statt einer Vorschau. Genau das war
 * der Anlass laut Spec: „heute muss man sich das Ergebnis vorstellen".
 */

const LEERE_OPTION = { text: '', correct: false }

export function Editor() {
  const suche = useSearch()
  const [, navigiere] = useLocation()
  const params = new URLSearchParams(suche)
  const id = params.get('id') || ''
  const istNeu = !id

  const [ordner, setOrdner] = useState(params.get('folder') || '')
  const [alleOrdner, setAlleOrdner] = useState([])
  const [front, setFront] = useState('')
  const [back, setBack] = useState('')
  const [zusatz, setZusatz] = useState('')
  const [typ, setTyp] = useState('normal')
  const [optionen, setOptionen] = useState([{ ...LEERE_OPTION }, { ...LEERE_OPTION }])
  const [anzeigename, setAnzeigename] = useState('')
  const [laedt, setLaedt] = useState(!istNeu)
  const [meldung, setMeldung] = useState(null)
  const [speichert, setSpeichert] = useState(false)
  const [laedtBild, setLaedtBild] = useState(false)
  // Welches Textfeld zuletzt die Schreibmarke hatte — dorthin geht das Bild.
  const aktivesFeld = useRef({ name: 'front', element: null })

  // Ordnerliste für die Themenauswahl.
  useEffect(() => {
    api
      .get('/api/cards', {})
      .then((daten) => setAlleOrdner(daten.folders ?? []))
      .catch(() => setAlleOrdner([]))
  }, [])

  // Vorhandene Karte laden.
  useEffect(() => {
    if (istNeu) return
    setLaedt(true)
    api
      .get('/api/cards/one', { id })
      .then((karte) => {
        setFront(karte.front || '')
        setBack(karte.back || '')
        setZusatz(karte.back_extra || '')
        setTyp(karte.card_type || 'normal')
        setAnzeigename(karte.display_title || '')
        setOrdner(karte.id.includes('/') ? karte.id.slice(0, karte.id.lastIndexOf('/')) : '')
        if (karte.card_type === 'mc' && karte.options?.length) {
          setOptionen(karte.options.map((o) => ({ text: o.text, correct: !!o.correct })))
        }
        setLaedt(false)
      })
      .catch((f) => {
        setMeldung(f.message)
        setLaedt(false)
      })
  }, [id, istNeu])

  function setzeOption(index, aenderung) {
    setOptionen((alt) => alt.map((o, i) => (i === index ? { ...o, ...aenderung } : o)))
  }

  /** Genau eine Option ist richtig — wie im bestehenden Kartenformat. */
  function markiereRichtig(index) {
    setOptionen((alt) => alt.map((o, i) => ({ ...o, correct: i === index })))
  }

  const felder = { front: setFront, back: setBack, zusatz: setZusatz }
  const werte = { front, back, zusatz }

  /**
   * Text an der Schreibmarke des zuletzt benutzten Feldes einsetzen.
   *
   * Bewusst keine feste Zuordnung: ein Bild gehört mal auf die Vorderseite
   * („Welches Land?"), mal in die Antwort, mal in den Zusatz. Bei einer
   * MC-Karte gibt es kein Antwortfeld — dann zurück auf die Vorderseite.
   */
  function einfuegen(text) {
    const ziel = aktivesFeld.current
    const name = ziel.name === 'back' && typ === 'mc' ? 'front' : ziel.name
    const wert = werte[name] ?? ''
    const element = ziel.name === name ? ziel.element : null
    const pos = typeof element?.selectionStart === 'number' ? element.selectionStart : wert.length
    felder[name](`${wert.slice(0, pos)}${text}${wert.slice(pos)}`)
  }

  function bildHochladen(datei, feld) {
    if (!datei) return
    setMeldung(null)
    setLaedtBild(true)

    const formular = new FormData()
    formular.append('file', datei)
    fetch('/api/bilder', { method: 'POST', body: formular })
      .then(async (antwort) => {
        const rumpf = await antwort.json().catch(() => null)
        if (!antwort.ok) throw new Error(rumpf?.message || 'Das Bild wurde abgelehnt')
        return rumpf
      })
      .then((rumpf) => {
        einfuegen(`\n\n![](${rumpf.image})\n\n`)
        setLaedtBild(false)
      })
      .catch((f) => {
        setMeldung(f.message)
        setLaedtBild(false)
      })
      .finally(() => {
        // Sonst löst dieselbe Datei beim zweiten Mal kein change aus.
        if (feld) feld.value = ''
      })
  }

  const speichern = useCallback(() => {
    if (!front.trim()) {
      setMeldung('Die Vorderseite darf nicht leer sein.')
      return
    }

    const rumpf = {
      folder: ordner,
      front: front.trim(),
      back: typ === 'mc' ? '' : back.trim(),
      back_extra: zusatz.trim(),
      card_type: typ,
      display_title: anzeigename.trim(),
    }
    if (typ === 'mc') {
      rumpf.options = optionen
        .filter((o) => o.text.trim())
        .map((o) => ({ text: o.text.trim(), correct: !!o.correct }))
    }

    setMeldung(null)
    setSpeichert(true)
    const anfrage = istNeu
      ? api.post('/api/cards', rumpf)
      : api.put('/api/cards', { ...rumpf, id })

    anfrage
      .then(() => {
        setSpeichert(false)
        navigiere('/karten')
      })
      .catch((f) => {
        setSpeichert(false)
        setMeldung(f.message)
      })
  }, [front, back, zusatz, typ, optionen, ordner, anzeigename, istNeu, id, navigiere])

  if (laedt) return <Laden />

  return (
    <Seite titel={istNeu ? 'Neue Karte' : 'Karte bearbeiten'}>
      {meldung ? (
        <p class="zustand zustand--fehler" role="alert">
          {meldung}
        </p>
      ) : null}

      <div class="editor">
        <div class="editor__formular">
          <label class="feld">
            <span class="feld__label meta">Thema</span>
            <input
              class="feld__eingabe"
              list="ordnerliste"
              value={ordner}
              onInput={(e) => setOrdner(e.currentTarget.value)}
              placeholder="z. B. Afrika"
            />
            <datalist id="ordnerliste">
              {alleOrdner.map((o) => (
                <option key={o} value={o} />
              ))}
            </datalist>
          </label>

          <label class="feld">
            <span class="feld__label meta">Kartentyp</span>
            <select
              class="feld__eingabe"
              value={typ}
              onChange={(e) => setTyp(e.currentTarget.value)}
            >
              <option value="normal">Normal</option>
              <option value="mc">Multiple Choice</option>
            </select>
          </label>

          <div class="editor__bild">
            <label class={`knopf knopf--still knopf--klein ${laedtBild ? 'knopf--wartet' : ''}`}>
              {laedtBild ? 'Bild wird hochgeladen …' : 'Bild einfügen'}
              <input
                type="file"
                accept="image/*"
                class="visuell-versteckt"
                disabled={laedtBild}
                onChange={(e) => bildHochladen(e.currentTarget.files?.[0], e.currentTarget)}
              />
            </label>
            <span class="meta">
              Landet im zuletzt benutzten Feld an der Schreibmarke, als
              {' '}<code class="md-code">![](_bilder/…)</code>.
            </span>
          </div>

          <label class="feld">
            <span class="feld__label meta">Vorderseite</span>
            <textarea
              class="feld__eingabe"
              value={front}
              onFocus={(e) => { aktivesFeld.current = { name: 'front', element: e.currentTarget } }}
              onInput={(e) => setFront(e.currentTarget.value)}
            />
          </label>

          {typ === 'mc' ? (
            <fieldset class="editor__optionen">
              <legend class="feld__label meta">Optionen</legend>
              {optionen.map((option, i) => (
                <div class="editor__option" key={i}>
                  <label class="feld editor__option-text">
                    <span class="visuell-versteckt">Option {i + 1}</span>
                    <input
                      class="feld__eingabe"
                      value={option.text}
                      onInput={(e) => setzeOption(i, { text: e.currentTarget.value })}
                    />
                  </label>
                  <label class="editor__option-richtig meta">
                    <input
                      type="radio"
                      name="richtige-option"
                      checked={option.correct}
                      aria-label={`Option ${i + 1} ist richtig`}
                      onChange={() => markiereRichtig(i)}
                    />
                    richtig
                  </label>
                  <button
                    type="button"
                    class="knopf knopf--still knopf--klein"
                    aria-label={`Option ${i + 1} entfernen`}
                    disabled={optionen.length <= 2}
                    onClick={() => setOptionen((alt) => alt.filter((_, j) => j !== i))}
                  >
                    ×
                  </button>
                </div>
              ))}
              <button
                type="button"
                class="knopf knopf--still knopf--klein"
                onClick={() => setOptionen((alt) => [...alt, { ...LEERE_OPTION }])}
              >
                Option hinzufügen
              </button>
            </fieldset>
          ) : (
            <label class="feld">
              <span class="feld__label meta">Antwort</span>
              <textarea
                class="feld__eingabe"
                value={back}
                onFocus={(e) => { aktivesFeld.current = { name: 'back', element: e.currentTarget } }}
                onInput={(e) => setBack(e.currentTarget.value)}
              />
            </label>
          )}

          <label class="feld">
            <span class="feld__label meta">
              {typ === 'mc' ? 'Erklärung (Zusatz)' : 'Zusatz (optional)'}
            </span>
            <textarea
              class="feld__eingabe"
              value={zusatz}
              onFocus={(e) => { aktivesFeld.current = { name: 'zusatz', element: e.currentTarget } }}
              onInput={(e) => setZusatz(e.currentTarget.value)}
            />
          </label>

          <label class="feld">
            <span class="feld__label meta">Anzeigename (optional)</span>
            <input
              class="feld__eingabe"
              value={anzeigename}
              onInput={(e) => setAnzeigename(e.currentTarget.value)}
            />
          </label>

          <p class="meta">
            Formatierung: <code class="md-code">**fett**</code>,{' '}
            <code class="md-code">*kursiv*</code>, <code class="md-code">==Hervorhebung==</code>,{' '}
            <code class="md-code">[c:#0e5c58]Farbe[/c]</code>, Listen mit{' '}
            <code class="md-code">- </code>
          </p>

          <div class="editor__aktionen">
            <button
              type="button"
              class="knopf knopf--haupt"
              disabled={speichert}
              onClick={speichern}
            >
              Speichern
            </button>
            <a class="knopf knopf--still" href="/karten">
              Abbrechen
            </a>
          </div>
        </div>

        {/* Gleiche Breite und gleicher Renderer wie im Lernbildschirm. */}
        <div class="editor__vorschau">
          <p class="meta editor__vorschau-titel">Vorschau</p>
          <KartenMarkdown class="kartentext__frage" text={front} />

          {typ === 'mc' ? (
            <ul class="mc__optionen">
              {optionen
                .filter((o) => o.text.trim())
                .map((o, i) => (
                  <li key={i}>
                    <span class={`mc-option ${o.correct ? 'mc-option--richtig' : ''}`}>
                      {o.text}
                    </span>
                  </li>
                ))}
            </ul>
          ) : back ? (
            <>
              <hr class="kartentext__trenner" />
              <KartenMarkdown class="kartentext__antwort" text={back} />
            </>
          ) : null}

          {zusatz ? <KartenMarkdown class="kartentext__zusatz" text={zusatz} /> : null}
        </div>
      </div>
    </Seite>
  )
}
